import React, { useCallback, useContext } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import { getCurrentDateLabel, getCurrentOccupancyLabel } from '../../FlashHotel/FlashHotelMobileVariant/FlashHotelMobileVariantUtils'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import ModalContext from 'contexts/ModalContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import OfferCapacityWithAgesSelection from '../../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BedbankSidebarFilterCalendar from '../BedbankSidebarFilterCalendar'
import { getCapacitiesForBedbankOffer } from 'lib/offer/offerUtils'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { BusinessTravellerMultiRoomHotelsOfferPageMobile, BusinessTravellerMultiRoomHotelsOfferPageMobileDisabled } from 'businessTraveller/components/select-traveller/multi-room-hotels'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import BusinessTravellerSelectLoadingSkeleton from 'businessTraveller/components/select-traveller/BusinessTravellerSelectLoadingSkeleton'

interface Props {
  offer: App.BedbankOffer;
  dateLabel: string;
  occupancyLabel: string;
}

function BedbankMobileStickyHeaderFilters(props: Props) {
  const {
    offer,
    dateLabel,
    occupancyLabel,
  } = props

  const isMultiRoomEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtMultiRoomHotel)

  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const capacities = getCapacitiesForBedbankOffer(offer)

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.closeHeaderFilter })
  }, [dispatch])

  const onOpenCalendar = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'checkin' })

    showModal(<ModalBase mode="drawer"><BedbankSidebarFilterCalendar /></ModalBase>).then(onClose)
  }, [dispatch, showModal, onClose])

  const onOpenCapacity = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'capacity' })

    showModal(<ModalBase mode="drawer">
      <ModalHeader title="Who is going?" />
      <ModalBody>
        <OfferCapacityWithAgesSelection
          capacities={capacities}
        />
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Confirm',
        }}
      />
    </ModalBase>).then(onClose)
  }, [dispatch, showModal, capacities, onClose])

  return (
    <Group direction="horizontal" gap={8} wrap="wrap">
      {isMultiRoomEnabled && <BusinessTravellerAccountGuard
        employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
        loading={<BusinessTravellerSelectLoadingSkeleton />}
        fallback={<BusinessTravellerMultiRoomHotelsOfferPageMobileDisabled />}
      >
        <BusinessTravellerMultiRoomHotelsOfferPageMobile />
      </BusinessTravellerAccountGuard>}
      <FilterChip
        startIcon={<LineCalendarIcon />}
        size="medium"
        onClick={onOpenCalendar}
        data-testid="OfferDatePickerSelectBox"
      >
      {dateLabel || 'Anytime'}
      </FilterChip>
      {!isMultiRoomEnabled && occupancyLabel && <FilterChip
        startIcon={<LineUsersAltIcon />}
        size="medium"
        onClick={onOpenCapacity}
      >
        {occupancyLabel}
      </FilterChip>}
    </Group>
  )
}

export default withOfferPageAndReduxState((offerState) => ({
  offer: offerState.bedbankOffer!,
  dateLabel: getCurrentDateLabel(offerState),
  occupancyLabel: getCurrentOccupancyLabel(offerState),
}), BedbankMobileStickyHeaderFilters)
